import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useCreateInsuranceDraftFetchMutation,
  useUpdateInsuranceMutation,
  useDeleteInsuranceDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { CommonToast } from "../../../common/Toast";
import { liveSectionRows, mergeSectionDrafts, sectionRecordOf } from "./sectionDrafts";
import { INSURANCE_SPEC } from "./insuranceFields";

/* Insurance's data.
 *
 * TRANSCRIBED from Insurance.jsx's effect, its `companyStatus()` and its two
 * delete branches. The draft merge is the flow-wide one.
 */
export const useInsuranceSection = ({ onStatusChange } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [draftRows, setDraftRows] = useState([]);

  const [fetchDrafts] = useCreateInsuranceDraftFetchMutation();
  const [updateInsurance] = useUpdateInsuranceMutation();
  const [deleteInsuranceDraft] = useDeleteInsuranceDraftMutation();

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await fetchDrafts({ companyID, token });
      setDraftRows(
        mergeSectionDrafts(res?.data?.data || [], currentEditingCompany, INSURANCE_SPEC)
      );
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. Falls back to what is already on record. */
      setDraftRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token, currentEditingCompany]);

  useEffect(() => {
    if (!currentEditingCompany) return;
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany]);

  const usingDrafts = draftRows.length > 0;
  const rows = usingDrafts
    ? draftRows
    : liveSectionRows(currentEditingCompany, INSURANCE_SPEC).map((row) =>
        sectionRecordOf(row, INSURANCE_SPEC)
      );

  const refresh = () => {
    /* A bare refetch() promise, rejected, surfaces as an unhandled rejection
       the dev overlay reports as "[object Object]". */
    Promise.resolve(refetch()).catch(() => {});
    loadDrafts();
  };

  /* Transcribed: a draft row is deleted outright and a null body counts as
     success; a live row is SOFT-deleted with is_deleted: 1, sent as FormData
     exactly as the live handler does. */
  const remove = async (record) => {
    try {
      if (record?.[INSURANCE_SPEC.draftIdKey]) {
        const res = await deleteInsuranceDraft({
          data: record[INSURANCE_SPEC.draftIdKey],
        });
        if (res?.data == null) {
          CommonToast.success(INSURANCE_SPEC.deleteMessage);
          refresh();
        }
        return;
      }

      if (record?.[INSURANCE_SPEC.idKey]) {
        const formData = new FormData();
        formData.append("is_deleted", 1);
        const res = await updateInsurance({
          data: formData,
          insuranceID: record[INSURANCE_SPEC.idKey],
          token,
        }).unwrap();

        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success(INSURANCE_SPEC.deleteMessage);
          refresh();
        }
      }
    } catch (error) {
      /* The live handler reports with alert() and has no catch on the
         unwrapped call. DRF's refusal arrives as `data.detail`. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't delete this insurance record."
      );
    }
  };

  return { rows, usingDrafts, remove, refresh };
};

export default useInsuranceSection;
